import { sectionRenderer } from "@theme/section-renderer";
import { Component } from "@theme/component";
import { FilterUpdateEvent } from "@theme/events";
import { debounce, startViewTransition } from "@theme/utilities";

/** Pixels tolerance when comparing scroll width to the scrollport (client) width. */
const OVERFLOW_EPSILON = 4;

const MEASURE_ROW_CLASS = "visual-filter-cards__slides--measure-row";

/** Debounce wait (ms) for layout-driven slider mode sync — matches resize-heavy patterns in facets.js. */
const SLIDER_SYNC_DEBOUNCE_MS = 100;

/**
 * Handles visual filter card interactions. Toggles filter URL params on click
 * and triggers section re-rendering to update both visual cards and sidebar filters.
 *
 * When cards fit in the scroll row, the slideshow wraps and hides progress/arrows;
 * when scrollWidth exceeds the row width, slider controls apply.
 *
 * @extends {Component}
 */
class VisualFilterCardsComponent extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof debounce<() => void>> | null} */
  #debouncedSyncSliderModes = null;

  connectedCallback() {
    super.connectedCallback();
    this.#debouncedSyncSliderModes = debounce(
      () => this.#syncVisualFilterSliderModes(),
      SLIDER_SYNC_DEBOUNCE_MS,
    );
    this.#resizeObserver = new ResizeObserver(() => {
      this.#debouncedSyncSliderModes?.();
    });
    this.#resizeObserver.observe(this);
    window.visualViewport?.addEventListener("resize", this.#onViewportResize);
    window.addEventListener("resize", this.#onViewportResize);
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#syncVisualFilterSliderModes());
      });
    });
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => this.#syncVisualFilterSliderModes());
  }

  disconnectedCallback() {
    window.visualViewport?.removeEventListener(
      "resize",
      this.#onViewportResize,
    );
    window.removeEventListener("resize", this.#onViewportResize);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#debouncedSyncSliderModes?.cancel();
    this.#debouncedSyncSliderModes = null;
    super.disconnectedCallback();
  }

  #onViewportResize = () => {
    this.#debouncedSyncSliderModes?.();
  };

  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id)
      throw new Error(
        "VisualFilterCardsComponent requires a section-id attribute",
      );
    return id;
  }

  #syncVisualFilterSliderModes() {
    const slideshows = this.querySelectorAll(
      "slideshow-component.visual-filter-cards__slider",
    );

    for (const slideshow of slideshows) {
      const scroller = slideshow.querySelector(".visual-filter-cards__slides");
      if (!(scroller instanceof HTMLElement)) continue;

      scroller.classList.add(MEASURE_ROW_CLASS);
      const contentWidth = scroller.scrollWidth;
      const rowWidth = scroller.clientWidth;
      scroller.classList.remove(MEASURE_ROW_CLASS);

      const overflows = contentWidth - rowWidth > OVERFLOW_EPSILON;

      slideshow.setAttribute(
        "data-visual-filters-mode",
        overflows ? "slider" : "fit",
      );
    }
  }

  /**
   * Toggles a filter param in the URL and re-renders the section.
   * @param {Event} event
   */
  toggleFilter(event) {
    if (
      event instanceof KeyboardEvent &&
      event.key !== "Enter" &&
      event.key !== " "
    )
      return;
    if (event instanceof KeyboardEvent) event.preventDefault();

    const card = event.target.closest("[data-param-name]");
    if (!card || card.disabled) return;

    const paramName = card.dataset.paramName;
    const paramValue = card.dataset.value;
    const isActive = card.dataset.active === "true";

    const url = new URL(window.location.href);
    const newParams = new URLSearchParams();

    if (isActive) {
      for (const [key, value] of url.searchParams.entries()) {
        if (key === paramName && value === paramValue) continue;
        newParams.append(key, value);
      }
    } else {
      for (const [key, value] of url.searchParams.entries()) {
        if (key === paramName) continue;
        newParams.append(key, value);
      }
      newParams.append(paramName, paramValue);
    }

    newParams.delete("page");
    url.search = newParams.toString();

    history.pushState(
      { urlParameters: url.searchParams.toString() },
      "",
      url.toString(),
    );

    this.dispatchEvent(new FilterUpdateEvent(url.searchParams));

    const sectionId = this.sectionId;
    startViewTransition(
      () =>
        sectionRenderer
          .renderSection(sectionId, {
            cache: false,
            url,
          })
          .catch(() => {
            window.location.assign(url.toString());
          }),
      ["product-grid"],
    );
  }
}

if (!customElements.get("visual-filter-cards-component")) {
  customElements.define(
    "visual-filter-cards-component",
    VisualFilterCardsComponent,
  );
}
